import type {
  RawConsentsListResponse,
  RawProfileConsentResponse,
  RawUpdateProfileConsentRequest,
} from '@heviton/heviton-api-app-legacy';
import type { ConsentDoc, UserConsent } from '@/shared/data-types';

/**
 * BE `RawProfileConsentResponse` → FE `UserConsent[]`. **순수 변환** — nullable wire 를 비-null 로
 * graceful 격하(누락 `agreed`=false, `consentId`='' — UI 가 CLU11 등 코드로 필터).
 * `sequence` 오름차순 정렬(`fromRawConsentDocs` 정합) — BE 응답 순서는 보장이 아니라 동의 온보딩
 * 화면이 매번 같은 순서를 보이도록 매퍼가 확정한다.
 */
export function fromRawConsents(raw: RawProfileConsentResponse): UserConsent[] {
  return (raw.consents ?? [])
    .map((consent) => ({
      consentId: consent.consentId ?? '',
      name: consent.name ?? undefined,
      agreed: consent.agreed ?? false,
      required: consent.required,
      sequence: consent.sequence ?? Number.MAX_SAFE_INTEGER,
    }))
    .sort((a, b) => a.sequence - b.sequence);
}

/**
 * BE 약관 개정일(한글 완성형, 예: '2026년 07월 09일 16시 22분 11초') → 'YYYY.MM.DD'.
 * 앞 3개 숫자군(년/월/일)만 추출·zero-pad, 시·분·초는 버림. 형식 불일치·결측 시 원문 trim 폴백(정보 보존).
 */
function formatConsentRevisedDate(raw: string | null | undefined): string {
  if (!raw) return '';
  const match = raw.match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
  const [, year, month, day] = match ?? [];
  if (!year || !month || !day) return raw.trim();
  return `${year}.${month.padStart(2, '0')}.${day.padStart(2, '0')}`;
}

/**
 * BE `RawConsentsListResponse` → FE `ConsentDoc[]`. **순수 변환** — 약관 목록(원문 content 인라인).
 * `consentId` 결측 노드는 제외(레거시 정합), `sequence` 오름차순 정렬. null 필드는 빈 값으로 격하.
 * `date` 는 한글 개정일 문자열을 'YYYY.MM.DD' 로 정규화.
 */
export function fromRawConsentDocs(raw: RawConsentsListResponse): ConsentDoc[] {
  return (raw.consents ?? [])
    .filter((consent) => consent.consentId)
    .map((consent) => ({
      consentId: consent.consentId ?? '',
      name: consent.name ?? '',
      content: consent.content ?? '',
      sequence: consent.sequence ?? Number.MAX_SAFE_INTEGER,
      date: formatConsentRevisedDate(consent.date),
    }))
    .sort((a, b) => a.sequence - b.sequence);
}

/**
 * FE `UserConsent[]` → BE `RawUpdateProfileConsentRequest` (PUT body). 전체 목록을 되돌려보내며
 * 토글된 `agreed` 만 반영 — 서버 스냅샷 보존(feature 가 전체 배열을 넘긴다).
 */
export function toRawConsents(consents: UserConsent[]): RawUpdateProfileConsentRequest {
  return {
    consents: consents.map((consent) => ({
      consentId: consent.consentId,
      required: consent.required,
      agreed: consent.agreed,
    })),
  };
}
